<template>
  <div class="kpt-detail-content-page" v-if="device">
    <vue-headful :title="`${device.serial} - Software`" />
    <div class="kpt-detail-content-actions">
      <div class="buttons">
        <b-tooltip position="is-right" :label="updateSoftwareTooltipMessage" :active="updateSoftwareTooltipMessage != null" style="margin-right: 8px">
          <b-button type="is-primary is-outlined" icon-left="upload" size="is-small" v-if="!updatingSoftwareVisible && $hasRole('Super User,Kymeta Admin,Product Support')" :disabled="isUpdatingSoftware || currentVersionMissing || isLocked" @click="showUpdateSoftware">Update Software</b-button>
        </b-tooltip>
        <b-button type="is-primary is-outlined" icon-left="window-close" size="is-small" v-if="!updatingSoftwareVisible && device.software.targetVersion" :disabled="isUpdatingSoftware" @click="cancelUpdateSoftware" :loading="isCancellingUpdate">Cancel Pending Update</b-button>
        <b-button type="is-primary is-outlined" icon-left="upload" size="is-small" v-if="updatingSoftwareVisible" :disabled="isUpdatingSoftware || !targetVersion" :loading="isUpdatingSoftware" @click="scheduleUpdateSoftware">Schedule Update</b-button>
        <b-button type="is-primary is-outlined" icon-left="times" size="is-small" v-if="updatingSoftwareVisible" :disabled="isUpdatingSoftware" @click="hideUpdateSoftware">Cancel</b-button>
        <b-button type="is-primary is-outlined" :icon-left="device.software.isLocked ? 'unlock' : 'lock'" size="is-small" v-if="!updatingSoftwareVisible" :loading="isTogglingLock" :disabled="isTogglingLock || !(device.software)" @click="toggleLock">
          <span v-if="device.software && !device.software.isLocked">Disable Software Updates</span>
          <span v-else>Enable Software Updates</span>
        </b-button>
      </div>
    </div>
    <div v-if="!updatingSoftwareVisible">
      <header>
        <h6 class="is-uppercase"><strong>Current Software</strong></h6>
      </header>
      <p v-if="!device.software">No software information available.</p>
      <div v-else>
        <detail-field>
          <template v-slot:label>Current Version</template>
          <template v-slot:value><font-awesome-icon :icon="['far', 'lock']" title="Software version is locked" v-if="device.software.isLocked" /> {{device.software.currentVersion || 'Unknown'}}</template>
        </detail-field>
        <detail-field>
          <template v-slot:label>Updated On</template>
          <template v-slot:value>{{device.software.updatedOn ? format(new Date(device.software.updatedOn), 'yyyy-MM-dd HH:mm:ss') : '-'}}</template>
        </detail-field>
        <detail-field>
          <template v-slot:label>Locked</template>
          <template v-slot:value>{{device.software.isLocked ? 'Yes' : 'No'}}</template>
        </detail-field>
        <div v-if="device.software.targetVersion">
          <hr>
          <header>
            <h6 class="is-uppercase"><strong>Pending Software Update</strong></h6>
          </header>
          <detail-field>
            <template v-slot:label>Target Version</template>
            <template v-slot:value>{{device.software.targetVersion || '-'}}</template>
          </detail-field>
          <detail-field>
            <template v-slot:label>Scheduled On</template>
            <template v-slot:value>{{device.software.updateScheduledOn ? format(new Date(device.software.updateScheduledOn), 'yyyy-MM-dd HH:mm:ss') : '-'}}</template>
          </detail-field>
        </div>
      </div>
    </div>
    <div v-else>
      <b-message type="is-light" class="content">
        <p>You are updating an <strong>{{device.deviceType}}</strong> with the serial <strong>{{device.serial}}</strong> with the existing software version <strong>{{device.software.currentVersion}}</strong>.</p>
        <p>This antenna will be updated the next time it checks in with Kymeta Cloud Services.</p>
      </b-message>
      <label class="label">Choose the target version:</label>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-tooltip type="is-info" label="There are no available updates for this device." :active="isSoftwareVersionsDropdownDisabled" position="is-right">
              <b-select v-model="targetVersion" :disabled="isSoftwareVersionsDropdownDisabled">
                <option v-for="opt in softwareVersionsForThisDevice" :key="opt.id" :value="opt">{{opt.version}}</option>
              </b-select>
            </b-tooltip>
          </div>
          <div class="level-item" v-if="targetVersion">
            Released: {{format(new Date(targetVersion.releaseDate), 'yyyy-MM-dd')}}
          </div>
        </div>
      </div>
      <div v-if="targetVersion">
        <b-notification type="is-info" :closable="false">
          <p><strong>Note:</strong> The minimum required version for the {{targetVersion.version}} release is {{targetVersion.minimumRequiredVersion}}. Updating from releases older than {{targetVersion.minimumRequiredVersion}} may break the antenna.</p>
        </b-notification>
      </div>
    </div>
  </div>
</template>
<script>
import { orderBy } from 'lodash';
import { format } from 'date-fns';

export default {
  data() {
    return {
      isRefreshing: false,
      isTogglingLock: false,
      isUpdatingSoftware: false,
      updatingSoftwareVisible: false,
      isCancellingUpdate: false,
      targetVersion: null,
      format
    }
  },
  computed: {
    device() {
      return this.$store.state.devices.device;
    },
    software() {
      return this.device.software;
    },
    softwareVersionsForThisDevice() {
      const versionSpecific = this.$store.state.devices.filteredVersionsByDevice.find(v => v.deviceTypeId == this.device.deviceTypeId);
      if (versionSpecific) return orderBy(versionSpecific.allowedVersions, ['version'], ['asc']);
      return [];
    },
    currentVersionMissing() {
      return (!this.device.software) || (!this.device.software.currentVersion);
    },
    isLocked() {
      return this.device.software && this.device.software.isLocked
    },
    updateSoftwareTooltipMessage() {
      let message;
      if (this.currentVersionMissing) {
        message = 'Cannot update software when current version is missing.';
      } else if (this.isLocked) {
        message = 'Cannot update software when current version is locked.';
      }
      return message;
    },
    isSoftwareVersionsDropdownDisabled() {
      return this.softwareVersionsForThisDevice.length == 0
    }
  },
  methods: {
    async toggleLock() {
      if (!this.device.software) return;
      const cmd = this.device.software.isLocked ? 'unlockSoftware' : 'lockSoftware';
      this.isTogglingLock = true;
      await this.$store.dispatch('executeDeviceCommand', { id: this.device.id, cmd })
      this.isTogglingLock = false;
    },
    showUpdateSoftware() {
      this.updatingSoftwareVisible = true;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'showUpdateSoftware', 'Software update clicked', { deviceId: this.device.id });
    },
    hideUpdateSoftware() {
      this.targetVersion = null;
      this.updatingSoftwareVisible = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'hideUpdateSoftware', 'Hide software update clicked', { deviceId: this.device.id });
    },
    async scheduleUpdateSoftware() {
      this.isUpdatingSoftware = true;
      const result = await this.$store.dispatch('executeDeviceCommand', { 
        id: this.device.id, 
        cmd: 'updateSoftware', 
        targetVersion: this.targetVersion.version 
      });
      this.isUpdatingSoftware = false;
      if (result) {
        this.hideUpdateSoftware();
      }
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'scheduleUpdateSoftware', 'Scheduled software update', { deviceId: this.device.id });
    },
    async cancelUpdateSoftware() {
      this.isCancellingUpdate = true;
      await this.$store.dispatch('executeDeviceCommand', { id: this.device.id, cmd: 'cancelUpdateSoftware' })
      this.isCancellingUpdate = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'cancelDeviceUpdateSoftware', 'Cancelled devices software update', { deviceId: this.device.id });
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../styles/_mixins';

.kpt-software-update-grid {
  @include display-grid;
  grid-template-columns: 100px 50px 1fr;
}
</style>
